import React, { useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Selection,
  Sort,
  Inject,
} from '@syncfusion/ej2-react-grids';

import { EncounterType } from '../../../../../services/EncounterService/types/encounterTypes';
import {
  PatientTemplate,
  StatusTemplate,
  ServiceDateTemplate,
  VisitNumberTemplate,
  ActionButtonTemplate,
} from './templates';

export interface EncounterSearchGridProps {
  encounters: EncounterType[];
  onSelectEncounter: (encounter: EncounterType) => void;
  creatingRefundFor: string | null;
  isLoading?: boolean;
  testId?: string;
}

// Grid settings
const pageSettings = { pageSize: 15, pageSizes: [10, 15, 25, 50] };

/**
 * EncounterSearchGrid - Displays encounter search results for refund creation
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Clean component structure
 */
export const EncounterSearchGrid: React.FC<EncounterSearchGridProps> = ({
  encounters,
  onSelectEncounter,
  creatingRefundFor,
  isLoading = false,
  testId,
}) => {
  // Transform encounters data for grid
  const gridData = useMemo(
    () =>
      encounters.map((encounter) => ({
        id: encounter.id,
        patientName: `${encounter.patient?.firstName || ''} ${encounter.patient?.lastName || ''}`.trim(),
        birthDate: encounter.patient?.birthDate,
        mrn: encounter.patient?.mrn || 'N/A',
        accountNumber: encounter.caseIdentification?.accountNumber || 'N/A',
        serviceDate: encounter.encounterInfo?.serviceDate,
        status: encounter.status || 'Unknown',
        visitNumber: encounter.encounterInfo?.visitNumber || 'N/A',
        encounter: encounter,
      })),
    [encounters]
  );

  // Create templates
  const patientTemplate = useCallback(
    (props: any) => (
      <PatientTemplate patientName={props.patientName} birthDate={props.birthDate} />
    ),
    []
  );

  const statusTemplate = useCallback(
    (props: any) => <StatusTemplate status={props.status} />,
    []
  );

  const serviceDateTemplate = useCallback(
    (props: any) => <ServiceDateTemplate serviceDate={props.serviceDate} />,
    []
  );

  const visitNumberTemplate = useCallback(
    (props: any) => <VisitNumberTemplate visitNumber={props.visitNumber} />,
    []
  );

  const actionTemplate = useCallback(
    (props: any) => (
      <ActionButtonTemplate
        encounterId={props.id}
        isCreating={creatingRefundFor === props.id}
        onSelect={() => onSelectEncounter(props.encounter)}
      />
    ),
    [creatingRefundFor, onSelectEncounter]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 1.5,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: '#1e293b',
          p: 1.5,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          fontSize: '0.95rem',
        }}
      >
        Encounter Results ({encounters.length} found)
      </Typography>

      <GridComponent
        dataSource={gridData}
        allowPaging={true}
        allowSorting={true}
        pageSettings={pageSettings}
        height={450}
        rowHeight={40}
        gridLines="Both"
        style={{
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '0.875rem',
        }}
        cssClass="compact-grid"
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="patientName"
            headerText="Patient"
            width="160"
            template={patientTemplate}
          />
          <ColumnDirective field="mrn" headerText="MRN" width="90" />
          <ColumnDirective field="accountNumber" headerText="Account #" width="105" />
          <ColumnDirective
            field="serviceDate"
            headerText="Service Date"
            width="110"
            template={serviceDateTemplate}
          />
          <ColumnDirective
            field="status"
            headerText="Status"
            width="90"
            template={statusTemplate}
          />
          <ColumnDirective
            field="visitNumber"
            headerText="Visit #"
            width="85"
            template={visitNumberTemplate}
          />
          <ColumnDirective
            field="id"
            headerText="Action"
            width="100"
            template={actionTemplate}
            allowSorting={false}
          />
        </ColumnsDirective>
        <Inject services={[Page, Selection, Sort]} />
      </GridComponent>
    </Box>
  );
};

export default EncounterSearchGrid;
